import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

function UserPaymentForm() {
  const navigate = useNavigate();
  const location = useLocation();

  const userData = location.state || {};

  // ✅ GET userBookingId FROM PREVIOUS PAGE
  const userBookingId = userData.userBookingId || "";

  const userFullName =
    `${userData.firstName || ""} ${userData.lastName || ""}`.trim();

  const totalBedCost = userData.totalBedCost || userData.bedCost || 0;

  const [showPopup, setShowPopup] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    userFullName,
    totalBedAmount: totalBedCost,
    userPaidAmount: "",
    isAnyPendingAmount: false,
    paymentUpdatedBy: JSON.parse(localStorage.getItem("user"))?.emailId || "",
    paymentType: "",
  });

  const [loading, setLoading] = useState(false);

  // ================= AUTO PENDING =================
  useEffect(() => {
    const total = Number(form.totalBedAmount || 0);
    const paid = Number(form.userPaidAmount || 0);

    setForm((prev) => ({
      ...prev,
      isAnyPendingAmount: paid < total,
    }));
  }, [form.userPaidAmount, form.totalBedAmount]);

  // ================= HANDLE CHANGE =================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // remove error while typing
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const pendingAmount =
    Number(form.totalBedAmount || 0) - Number(form.userPaidAmount || 0);

  // ================= VALIDATION =================
  const validate = () => {
    let tempErrors = {};

    const total = Number(form.totalBedAmount || 0);
    const paid = Number(form.userPaidAmount || 0);

    if (!form.userPaidAmount || paid <= 0) {
      tempErrors.userPaidAmount = "Paid amount is required";
    }

    if (paid > total) {
      tempErrors.userPaidAmount = "Paid amount cannot be greater than total";
    }

    if (!form.paymentType) {
      tempErrors.paymentType = "Please select payment type";
    }

    setErrors(tempErrors);

    return Object.keys(tempErrors).length === 0;
  };

  // ================= SUBMIT =================
  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);

    const payload = {
      userBookingId: userBookingId,
      firstName: form.userFullName,
      totalBedAmount: Number(form.totalBedAmount),
      userPaidAmount: Number(form.userPaidAmount),
      isAnyPendingAmount: pendingAmount > 0,
      paymentUpdatedBy: form.paymentUpdatedBy,
      paymentType: form.paymentType,
    };

    // Read logged-in user
    const user = JSON.parse(localStorage.getItem("user"));

    // JWT Token
    const token = user?.jwtToken;

    try {
      const response = await fetch(
        "http://localhost:9777/v1/user/userPayment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      // Token Expired
      if (response.status === 401) {
        alert("Session Expired. Please login again.");

        localStorage.removeItem("user");

        navigate("/login");
        return;
      }

      const data = await response.json();

      if (data.statusCode === 200) {
        setShowPopup(true);

        setTimeout(() => {
          setShowPopup(false);
          navigate("/dashboard");
        }, 2000);
      } else {
        alert(data.message || "Payment Failed");
      }
    } catch (error) {
      console.error(error);
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {showPopup && <div style={styles.popup}>✅ Payment Successful</div>}

      <div style={styles.card}>
        <h1 style={styles.title}>💳 User Payment Form</h1>

        <div style={styles.grid}>
          {/* USER BOOKING ID */}

          {/* NAME */}
          <div style={styles.fieldBoxBlue}>
            <label style={styles.label}>User Full Name</label>

            <input value={form.userFullName} readOnly style={styles.input} />
          </div>

          {/* TOTAL */}
          <div style={styles.fieldBoxOrange}>
            <label style={styles.label}>Total Bed Cost</label>

            <input value={form.totalBedAmount} readOnly style={styles.input} />
          </div>

          {/* PAID */}
          <div style={styles.fieldBoxGreen}>
            <label style={styles.label}>Paid Amount</label>

            <input
              name="userPaidAmount"
              value={form.userPaidAmount}
              onChange={handleChange}
              style={styles.input}
            />

            {errors.userPaidAmount && (
              <p
                style={{
                  color: "red",
                  fontSize: "12px",
                }}
              >
                {errors.userPaidAmount}
              </p>
            )}
          </div>

          {/* TYPE */}
          <div style={styles.fieldBoxPurple}>
            <label style={styles.label}>Payment Type</label>

            <select
              name="paymentType"
              value={form.paymentType}
              onChange={handleChange}
              style={styles.input}
            >
              <option value="">Select</option>

              <option value="CASH">CASH</option>

              <option value="UPI">UPI</option>

              <option value="CARD">CARD</option>

              <option value="BANK_TRANSFER">BANK_TRANSFER</option>
            </select>

            {errors.paymentType && (
              <p
                style={{
                  color: "red",
                  fontSize: "12px",
                }}
              >
                {errors.paymentType}
              </p>
            )}
          </div>
        </div>

        {/* SUMMARY */}
        <div style={styles.summaryBox}>
          <div style={styles.summaryCard}>
            💰 Paid: ₹ {form.userPaidAmount || 0}
          </div>

          <div style={styles.summaryCard}>
            🛏 Total: ₹ {form.totalBedAmount || 0}
          </div>

          <div style={styles.summaryCard}>
            ⚠ Pending: ₹ {pendingAmount > 0 ? pendingAmount : 0}
          </div>

          <div style={styles.summaryCard}>
            {form.paymentType || "Payment Type"}
          </div>
        </div>

        {/* BUTTON */}
        <button onClick={handleSubmit} disabled={loading} style={styles.button}>
          {loading ? "Processing..." : "Submit Payment"}
        </button>
      </div>
    </div>
  );
}

export default UserPaymentForm;

/* ================= STYLES ================= */
const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(135deg,#dbeafe,#eff6ff,#f0f9ff)",
    padding: "30px",
    fontFamily: "Segoe UI",
  },

  popup: {
    position: "fixed",
    top: "20px",
    right: "20px",
    background: "#16a34a",
    color: "#fff",
    padding: "15px 25px",
    borderRadius: "12px",
    fontWeight: "700",
    zIndex: 9999,
    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
  },

  card: {
    width: "100%",
    maxWidth: "1100px",
    background: "#fff",
    borderRadius: "24px",
    padding: "40px",
  },

  title: {
    textAlign: "center",
    fontSize: "38px",
    fontWeight: "800",
    marginBottom: "30px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))",
    gap: "20px",
  },

  fieldBoxBlue: {
    background: "#dbeafe",
    padding: "16px",
    borderRadius: "16px",
  },

  fieldBoxGreen: {
    background: "#dcfce7",
    padding: "16px",
    borderRadius: "16px",
  },

  fieldBoxOrange: {
    background: "#ffedd5",
    padding: "16px",
    borderRadius: "16px",
  },

  fieldBoxPurple: {
    background: "#ede9fe",
    padding: "16px",
    borderRadius: "16px",
  },

  label: {
    fontWeight: "700",
    marginBottom: "8px",
    display: "block",
  },

  input: {
    width: "100%",
    padding: "12px",
    borderRadius: "10px",
    border: "1px solid #ccc",
  },

  summaryBox: {
    marginTop: "30px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
    gap: "15px",
  },

  summaryCard: {
    background: "#2563eb",
    color: "#fff",
    padding: "18px",
    borderRadius: "14px",
    textAlign: "center",
    fontWeight: "700",
  },

  button: {
    marginTop: "30px",
    width: "100%",
    padding: "16px",
    background: "#4f46e5",
    color: "#fff",
    fontSize: "18px",
    fontWeight: "700",
    border: "none",
    borderRadius: "14px",
    cursor: "pointer",
  },
};
